import { ComponentProps, PropsWithChildren } from 'react';
import { Link, Route } from 'react-router-dom';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';
import { Container as PlainContainer } from './Container';
import { Count } from './Count';

const Sticky = styled(Box)<{ backgroundColor?: string }>`
	position: sticky;
	z-index: 2;
	top: 0;
	background: ${({ backgroundColor }) => backgroundColor ?? tokens.surface.white};
	flex-shrink: 0;
	flex-grow: 0;
`;

const UnderlineBar = styled(Box)`
	width: max-content;
	min-width: 100%;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const Item = styled.div`
	display: flex;
	flex: 0 0 auto;
	min-width: max-content;
	align-self: stretch;

	a {
		display: flex;
		align-self: stretch;
		height: 100%;
	}
`;

const Container = styled(Flex)<{ selected?: boolean }>`
	font-size: 15px;
	line-height: 1;
	font-family: ${tokens.typography.fontFamily};
	align-items: center;
	align-self: stretch;
	gap: ${tokens.gap.element}px;
	flex: 1 0 auto;
	min-width: auto;
	width: max-content;
	border-bottom: 2px solid transparent;
	cursor: pointer;
	padding: ${tokens.gap.group}px ${tokens.gap.group}px 20px ${tokens.padding.container}px;
	box-sizing: border-box;

	${(props) =>
		props.selected
			? `
		font-weight: ${tokens.typography.weights.semibold};
		color: ${tokens.text.primary};
		border-bottom-color: ${props.theme.colors.primary};
	`
			: `
		color: ${tokens.text.secondary};
		font-weight: 450;

		&:hover,
		&[data-preview='hover'] {
			color: ${tokens.text.primary};
			border-bottom-color: ${tokens.border.default};
		}
	`}
`;

export function Tabs(props: PropsWithChildren<BoxProps>) {
	return (
		<UnderlineBar>
			<Flex
				{...(props as ComponentProps<BoxType>)}
				mb="-1px"
				flexWrap="nowrap"
				flexShrink={0}
				width="max-content"
				minWidth="100%"
			>
				{props.children}
			</Flex>
		</UnderlineBar>
	);
}

Tabs.Container = function TabsContainer({
	wide,
	backgroundColor,
	children,
	...rest
}: PropsWithChildren<BoxProps & { wide?: number | boolean; backgroundColor?: string }>) {
	return (
		<Sticky backgroundColor={backgroundColor} overflowX="auto">
			<PlainContainer wide={wide}>
				<UnderlineBar>
					<Flex
						{...(rest as ComponentProps<BoxType>)}
						mb="-1px"
						flexWrap="nowrap"
						flexShrink={0}
						width="max-content"
						minWidth="100%"
					>
						{children}
					</Flex>
				</UnderlineBar>
			</PlainContainer>
		</Sticky>
	);
};

Tabs.Item = function TabItem(props: {
	title: string;
	selected?: boolean;
	path?: string[] | string;
	exact?: boolean;
	onClick?: () => void;
	counter?: number;
	'data-preview'?: string;
}) {
	const pathLink = Array.isArray(props.path) ? props.path[0] : props.path;
	// react router does not use query params for url matching. To make current-tab-selection work, we strip the params here.
	const pathToMatch = pathLink?.split('?')[0];

	const content = (
		<>
			{props.title}
			{props.counter != null ? <Count count={props.counter} /> : null}
		</>
	);

	return (
		<Item>
			{props.path ? (
				<Link to={pathLink}>
					<Route path={pathToMatch} exact={props.exact ?? true}>
						{({ match }) => <Container selected={!!match}>{content}</Container>}
					</Route>
				</Link>
			) : (
				<Container
					selected={props.selected}
					onClick={props.onClick}
					data-preview={props['data-preview']}
				>
					{content}
				</Container>
			)}
		</Item>
	);
};
